/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE OFFICER SURFACE'S BOUNDS. [P5a, relocated P5b]
 *
 *  ⚠️ [amended P5b] THESE LIVED IN `schema/officerApp.schema.ts` AND HAD TO MOVE.
 *
 *  They are plain numbers and a string tuple — CLAUDE.md §3's `*.constant.ts` — but the move
 *  was forced rather than tidy: `pwa/` reads two of them at RUNTIME so that its "All" chip
 *  cannot drift from the window the server actually applies, and every module in `schema/`
 *  imports zod. Importing them from there would have pulled the whole validation library into
 *  an app with a 150 KB budget, for two integers.
 *
 *  ⚠️ THIS IS A MOVE, NOT A COPY. Values, names and reasoning are byte-identical, and the
 *  schema now imports them from here. A second definition of a bound is how the app and the
 *  server come to disagree about what an officer's horizon is.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ 30 DAYS BACK AND 90 FORWARD, DECIDED BY THE SERVER.
 *
 * The past window is what makes "what was I on last month?" answerable at all; the forward
 * one is the bound the supplied contract left off. A standing order generates a duty a day
 * indefinitely, so an unbounded `dateTo` means an unbounded result set AND a `total` that
 * needs a full count on every page.
 */
export const APP_DUTIES_PAST_WINDOW_DAYS = 30;
export const APP_DUTIES_FUTURE_WINDOW_DAYS = 90;

/**
 * ⚠️ IT THROWS RATHER THAN NARROWING, and it is a server constant a caller cannot raise —
 * `INBOX_MAX_WINDOW_DAYS`' rule, for `INBOX_MAX_WINDOW_DAYS`' reason. A silently narrowed
 * window under-reports duties, and an officer who is not shown a duty does not turn up.
 *
 * ⚠️ AND IT MUST EXCEED THE DEFAULT SPAN, WHICH IS 121 DAYS INCLUSIVE. A cap below it would
 * make the no-argument request — the one the app sends on every cold start — a 422. That is
 * not a hypothetical: 92 was the obvious number to copy from the inbox, and it is wrong here.
 */
export const APP_DUTIES_MAX_WINDOW_DAYS = 180;

/**
 * ⚠️ A BOUND ON THE UNFILTERED FORM, AND IT NAMES ITS OWN WAY OUT.
 *
 * An arrangement may hold `MAX_DEPLOYMENTS_PER_ARRANGEMENT` duties, and an officer named on
 * all of them would be one unbounded payload. Refusing with a sentence that says "open one
 * duty at a time" is honest precisely because `deploymentId` exists — a refusal with no
 * alternative would just be a broken screen.
 *
 * Round-the-clock orders never reach it: the generator emits ONE arrangement per night.
 *
 * ⚠️ [P5b] `view=skeleton` IS NOT A WAY PAST THIS. It removes the roster, not the duties, and
 * the cap counts duties — so both views refuse at the same point. A second number for the
 * skeleton would be a second bound, and the whole argument for one is that a caller cannot
 * raise it.
 */
export const APP_DETAIL_MAX_DUTIES = 400;

/**
 * ⚠️ [P5b] TWO VIEWS, ONE PAYLOAD SHAPE. `appDutyDetailQuerySchema` carries the full argument;
 * the tuple is here because `pwa/` builds a request URL from it and must not retype a literal.
 */
export const APP_DETAIL_VIEWS = ['full', 'skeleton'] as const;
export type AppDetailView = (typeof APP_DETAIL_VIEWS)[number];

/**
 * ⚠️ [P6d] WHICH DUTIES THE DETAIL RETURNS — the caller's own, or the ORDER's.
 *
 * Here rather than in the schema for `APP_DETAIL_VIEWS`' reason: `pwa/` builds a request URL
 * from it and must not retype a literal. `appDutyDetailQuerySchema` carries the full argument
 * for why the two questions cannot be told apart by looking at who is asking.
 */
export const APP_DETAIL_INCLUDES = ['mine', 'order'] as const;
export type AppDetailInclude = (typeof APP_DETAIL_INCLUDES)[number];
